import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Flask } from '@phosphor-icons/react';
import { useAuth } from '@/auth/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/** Banner shown ONLY to the user whose personal テストモード is active.
 * Admins additionally see a subtle pill when someone else is in test mode.
 *
 * Hidden on full-screen print/kiosk routes because a "position: fixed;
 * z-index: 9999" banner without the "no-print" guard was making Safari's
 * window.print() silently no-op on /shizai/orders/*​/print while test mode
 * was active (Cmd+P still worked — pure JS-triggered print was blocked). Also
 * carries the "no-print" class so it never appears on the printed paper. */
export default function TestModeBanner() {
  const location = useLocation();
  const { user } = useAuth();
  const userId = user?.id;
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!userId) { setState(null); return undefined; }
    const check = () => axios.get(`${API}/api/test-mode`)
      .then((r) => { if (alive) setState(r.data); })
      .catch(() => { if (alive) setState(null); });
    check();
    const iv = setInterval(check, 60000);
    window.addEventListener('tm-changed', check);
    return () => { alive = false; clearInterval(iv); window.removeEventListener('tm-changed', check); };
  }, [userId]);
  const p = location.pathname || '';
  // Full-screen print/kiosk routes: don't render the fixed banner at all so
  // Safari's print dialog isn't blocked by a high-z-index fixed overlay.
  if (p.startsWith('/s/') || p.startsWith('/g/') || p.startsWith('/b/')) return null;
  if (p.endsWith('/print') || p.includes('/print/') || p.startsWith('/mfg/kiosk') || p.startsWith('/settings/credentials-print')) {
    return null;
  }
  if (state?.active) {
    const days = state.days_elapsed || 0;
    const overdue = days >= 7;
    return (
      <div className="no-print fixed bottom-0 inset-x-0 z-[9999] pointer-events-none">
        <div
          className={`flex items-center justify-center gap-1.5 text-center text-xs font-bold py-1.5 px-3 text-white ${overdue ? 'bg-red-600' : 'bg-orange-500'}`}
          data-testid="test-mode-banner"
        >
          <Flask size={14} weight="fill" />
          テストモード中（あなたのみ）— 終了時にあなたの変更はすべて破棄されます（開始: {(state.started_at || '').slice(0, 10)}
          {overdue ? ` / ${days}日経過 — 早めに終了してください` : ''}）
        </div>
      </div>
    );
  }
  if (state?.others?.length) {
    const names = state.others.map((o) => o.name).filter(Boolean).join('、');
    return (
      <div className="no-print fixed bottom-2 inset-x-0 z-[9999] flex justify-center pointer-events-none">
        <div
          className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-300 text-orange-700 text-[11px] font-semibold px-2.5 py-1 shadow-sm"
          data-testid="test-mode-others-pill"
        >
          <Flask size={12} weight="fill" />
          {names} がテストモード中（本人のみに適用）
        </div>
      </div>
    );
  }
  return null;
}
